import { InvoiceStatus, Role } from "elephant-web/gql";

export type InvoiceAction =
  | "void"
  | "takePayment"
  | "finalise"
  | "waive"
  | "exempt"
  | "refund"
  | "print"
  | "delete"
  | "paymentHistory";

const {
  Admin,
  CashOfficer,
  Clinic,
  Dispenser,
  Doctor,
  Nurse,
  Pharmacist,
  Technician,
} = Role;

// Role × action gate. State-level constraints live below in
// `ACTION_VALID_STATES`; this table is purely "which role is allowed
// to perform this action *at all*". `canPerformAction` enforces both.
//
// Mirror docs/billing/role-access-matrix.md. The unit test pins every
// cell — if you change one here, update the doc and the test together.
const ROLE_ACTION_MATRIX: Record<InvoiceAction, ReadonlyArray<Role>> = {
  // Matches api-billing/src/invoices/voidInvoice.ts @authUserRole(roles: [ADMIN, CLINIC])
  void: [Admin, Clinic],
  // Matches api-billing/src/invoices/takePayment.ts @authUserRole
  takePayment: [
    Admin,
    CashOfficer,
    Clinic,
    Dispenser,
    Doctor,
    Nurse,
    Pharmacist,
    Technician,
  ],
  // Matches api-billing/src/invoices/finalizeInvoice.ts @authUserRole
  finalise: [
    Admin,
    CashOfficer,
    Clinic,
    Dispenser,
    Doctor,
    Nurse,
    Pharmacist,
    Technician,
  ],
  // Waive is applied server-side via applyPayment; mirror finalise's set.
  waive: [
    Admin,
    CashOfficer,
    Clinic,
    Dispenser,
    Doctor,
    Nurse,
    Pharmacist,
    Technician,
  ],
  // Exemption follows the same route as waive (applyPayment).
  exempt: [
    Admin,
    CashOfficer,
    Clinic,
    Dispenser,
    Doctor,
    Nurse,
    Pharmacist,
    Technician,
  ],
  // Matches api-billing/src/invoices/refundInvoice.ts @authUserRole(roles: [CASH_OFFICER, ADMIN, CLINIC])
  refund: [Admin, CashOfficer, Clinic],
  // Print + payment-history are read-only; no server gate, mirror that.
  print: [
    Admin,
    CashOfficer,
    Clinic,
    Dispenser,
    Doctor,
    Nurse,
    Pharmacist,
    Technician,
  ],
  paymentHistory: [
    Admin,
    CashOfficer,
    Clinic,
    Dispenser,
    Doctor,
    Nurse,
    Pharmacist,
    Technician,
  ],
  // Deleting a draft is the inverse of creating one; same set as finalise.
  delete: [
    Admin,
    CashOfficer,
    Clinic,
    Dispenser,
    Doctor,
    Nurse,
    Pharmacist,
    Technician,
  ],
};

// State-level constraints — an action that never makes sense in a given
// state. Returns false here regardless of role. Buttons are also
// rendered conditionally per state by the action-row components, but
// codifying the constraint here keeps callers honest.
const ACTION_VALID_STATES: Record<
  InvoiceAction,
  ReadonlyArray<InvoiceStatus>
> = {
  void: [InvoiceStatus.Pending, InvoiceStatus.Unpaid],
  takePayment: [
    InvoiceStatus.Pending,
    InvoiceStatus.Unpaid,
    InvoiceStatus.PartiallyPaid,
  ],
  finalise: [InvoiceStatus.Draft],
  waive: [
    InvoiceStatus.Pending,
    InvoiceStatus.Unpaid,
    InvoiceStatus.PartiallyPaid,
  ],
  exempt: [
    InvoiceStatus.Pending,
    InvoiceStatus.Unpaid,
    InvoiceStatus.PartiallyPaid,
  ],
  refund: [
    InvoiceStatus.Paid,
    InvoiceStatus.PartiallyPaid,
    InvoiceStatus.PartiallyRefunded,
  ],
  delete: [InvoiceStatus.Draft],
  print: [
    InvoiceStatus.Draft,
    InvoiceStatus.Pending,
    InvoiceStatus.Unpaid,
    InvoiceStatus.PartiallyPaid,
    InvoiceStatus.Paid,
    InvoiceStatus.PartiallyRefunded,
    InvoiceStatus.Refunded,
    InvoiceStatus.Void,
  ],
  paymentHistory: [
    InvoiceStatus.Pending,
    InvoiceStatus.Unpaid,
    InvoiceStatus.PartiallyPaid,
    InvoiceStatus.Paid,
    InvoiceStatus.PartiallyRefunded,
    InvoiceStatus.Refunded,
    InvoiceStatus.Void,
  ],
};

export function canPerformAction(
  role: Role | undefined,
  action: InvoiceAction,
  state: InvoiceStatus,
): boolean {
  if (!role) return false;
  if (!ACTION_VALID_STATES[action].includes(state)) return false;
  return ROLE_ACTION_MATRIX[action].includes(role);
}
